import React from 'react';
import moment from 'moment';
import { Autocomplete, AutocompleteRenderInputParams, Box, TextField } from '@mui/material';
import { ReportingPeriod } from '@esg/esg-global-types';
import LockIcon from '@mui/icons-material/Lock';
import {
  getReportingPeriods,
  reporting_period_label
} from '../../../../lib/metric_capture/reporting_period';

interface ReportingPeriodSelectProps {
  handleChangeReportingPeriods?: (
    reporting_periods: Array<ReportingPeriod> | ReportingPeriod | null
  ) => void;
  selected_reporting_periods?: Array<ReportingPeriod> | ReportingPeriod | null;
  reporting_period_options?: Array<ReportingPeriod>;
  group_id?: string;
  company_id?: string;
  allow_multi_select?: boolean;
  tag_limit?: number;
  disabled?: boolean;
  hide_icons?: boolean;
  input_label?: string;
  variant?: 'standard' | 'outlined';
  clearable?: boolean;
  disable_selected_tags?: boolean;
}

/**
 * Reporting Period selection auto-complete component that handles a variety of built-in scenarios.
 * @param {Array<ReportingPeriod> | ReportingPeriod | null} props.handleChangeReportingPeriod Callback function to notify the parent component when the selected options have changed.
 * @param {Array<ReportingPeriod>} props.reporting_period_options Custom list of options provided by the parent component. Defaults to none.
 * @param {Array<ReportingPeriod> | ReportingPeriod | null} props.selected_reporting_periods Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {string} props.group_id Query configured reporting periods for the provided group. Works in conjunction with props.company_id. Optionally works in conjunction with unconfigured_only or standard_ids. Defaults to empty list.
 * @param {string} props.company_id Query configured reporting periods for the provided company. Works in conjunction with props.group_id. Optionally works in conjunction with unconfigured_only or standard_ids. Defaults to empty list.
 * @param {string} props.variant An optional styling parameter for the textbox input of the autocomplete.
 * @param {boolean} props.disable_selected_tags Optionally replace the selected tags with text when autocomplete is expanded to render input at a predictable height.
 * @returns {JSX.Element}
 */
const ReportingPeriodSelect = (props: ReportingPeriodSelectProps) => {
  const [reportingPeriodOptions, setReportingPeriodOptions] = React.useState<
    Array<ReportingPeriod>
  >(props.reporting_period_options ?? []);
  const [locked, setLocked] = React.useState<boolean>(
    props.selected_reporting_periods && !Array.isArray(props.selected_reporting_periods)
      ? props.selected_reporting_periods.locked
      : false
  );

  React.useEffect(() => {
    (async () => {
      if (props.reporting_period_options === undefined && props.group_id && props.company_id) {
        const reporting_periods: Array<ReportingPeriod> = await getReportingPeriods(
          props.group_id,
          props.company_id
        );
        setReportingPeriodOptions(reporting_periods);
      }
    })();
  }, [props.group_id, props.company_id]);

  return (
    <Box id="reporting-period-select">
      <Autocomplete
        fullWidth
        disabled={props.disabled}
        limitTags={props.tag_limit}
        disableClearable={props.clearable === false}
        options={
          props.reporting_period_options
            ? props.reporting_period_options.sort((a: ReportingPeriod, b: ReportingPeriod) =>
                a.name == 'Average Over Group'
                  ? -1
                  : moment(a.start).unix() - moment(b.start).unix()
              )
            : reportingPeriodOptions.sort((a: ReportingPeriod, b: ReportingPeriod) =>
                a.name == 'Average Over Group'
                  ? -1
                  : moment(a.start).unix() - moment(b.start).unix()
              )
        }
        disableCloseOnSelect={props.allow_multi_select}
        value={props.selected_reporting_periods}
        getOptionLabel={(option: ReportingPeriod) => {
          return option.name;
        }}
        renderOption={(render_props, option: ReportingPeriod) => (
          <Box
            component="li"
            sx={{ display: 'flex', alignItems: 'center' }}
            {...render_props}
            key={option.id}
          >
            {option.name}
            {option.locked && props.hide_icons !== true && (
              <LockIcon
                sx={{ mx: 2, position: 'relative', color: 'primary', fontSize: '1.3rem' }}
              />
            )}
          </Box>
        )}
        renderValue={
          !props.allow_multi_select || !props.disable_selected_tags
            ? undefined
            : (value: ReportingPeriod | Array<ReportingPeriod>) => {
                return Array.isArray(value) && value.length > 0
                  ? `${value.length} ${value.length === 1 ? reporting_period_label.one : reporting_period_label.many}`
                  : undefined;
              }
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        multiple={props.allow_multi_select}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            label={props.input_label ?? 'Reporting Period'}
            variant={props.variant ?? 'standard'}
            InputProps={{
              ...params.InputProps,
              endAdornment:
                props.allow_multi_select !== true &&
                props.hide_icons !== true &&
                (props.selected_reporting_periods === undefined ||
                  props.selected_reporting_periods !== null) &&
                locked ? (
                  <>
                    <LockIcon sx={{ color: 'primary', fontSize: '1.3rem' }} />
                    {params.InputProps.endAdornment}
                  </>
                ) : (
                  params.InputProps.endAdornment
                )
            }}
          />
        )}
        onChange={(
          event: React.SyntheticEvent<Element, Event>,
          value: Array<ReportingPeriod> | ReportingPeriod | null
        ) => {
          if (!props.allow_multi_select) {
            setLocked(value ? (value as ReportingPeriod).locked : false);
          }
          props.handleChangeReportingPeriods && props.handleChangeReportingPeriods(value);
        }}
      />
    </Box>
  );
};

export default ReportingPeriodSelect;
